import { useWizard } from '../../context/WizardContext';
import { motion } from 'framer-motion';
import { Users, Plus, Minus, Wallet, TrendingUp, FileText, Heart } from 'lucide-react';

const budgetRanges = [
  { value: '0-5000', label: 'Ate R$5.000' },
  { value: '5000-15000', label: 'R$5.000 - R$15.000' },
  { value: '15000-30000', label: 'R$15.000 - R$30.000' },
  { value: '30000-50000', label: 'R$30.000 - R$50.000' },
  { value: '50000+', label: 'Mais de R$50.000' }
];

const incomeRanges = [
  { value: '0-3000', label: 'Ate R$3.000/mes' },
  { value: '3000-7000', label: 'R$3.000 - R$7.000/mes' },
  { value: '7000-15000', label: 'R$7.000 - R$15.000/mes' },
  { value: '15000-30000', label: 'R$15.000 - R$30.000/mes' },
  { value: '30000+', label: 'Mais de R$30.000/mes' },
];

export const Step2Perfil = () => {
  const { data, updateFamily, updateFinance, nextStep, prevStep } = useWizard();
  const { family, finance } = data;

  const handleTotalChange = (delta) => {
    const newTotal = Math.max(1, Math.min(10, family.total + delta));
    const newAdults = Math.min(family.adults, newTotal);
    const newChildren = newTotal - newAdults;
    updateFamily({ total: newTotal, adults: newAdults, children: newChildren, ages: newChildren > 0 ? family.ages.slice(0, newChildren) : [] });
  };

  const toggleOption = (key) => {
    updateFinance({ [key]: !finance[key] });
  };

  const isValid = finance.budget_range && finance.income_range;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-5"
    >
      {/* Family Size - Compact */}
      <div>
        <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
          <Users className="w-4 h-4 text-[#C9A24A]" />
          Quantas pessoas viajam? (incluindo voce)
        </label>
        <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
          <button type="button" onClick={() => handleTotalChange(-1)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C9A24A]/20">
            <Minus className="w-4 h-4 text-gray-400" />
          </button>
          <span className="text-2xl font-bold text-[#C9A24A] w-8 text-center">{family.total}</span>
          <button type="button" onClick={() => handleTotalChange(1)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C9A24A]/20">
            <Plus className="w-4 h-4 text-gray-400" />
          </button>
          <span className="text-sm text-gray-500 ml-2">
            {family.total === 1 ? 'pessoa' : 'pessoas'}
            {family.total > 1 && ` (${family.total - 1} dependente${family.total > 2 ? 's' : ''})`}
          </span>
        </div>
      </div>

      {/* Budget */}
      <div>
        <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
          <Wallet className="w-4 h-4 text-[#C9A24A]" />
          Orcamento para o processo
        </label>
        <div className="grid grid-cols-2 gap-2">
          {budgetRanges.map((range) => (
            <button
              key={range.value}
              type="button"
              onClick={() => updateFinance({ budget_range: range.value })}
              className={`p-2.5 rounded-lg border text-sm transition-colors ${
                finance.budget_range === range.value
                  ? 'bg-[#C9A24A]/20 border-[#C9A24A] text-[#C9A24A]'
                  : 'bg-white/5 border-white/10 text-gray-300 hover:border-[#C9A24A]/50'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Income */}
      <div>
        <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
          <TrendingUp className="w-4 h-4 text-[#C9A24A]" />
          Renda mensal
        </label>
        <div className="grid grid-cols-2 gap-2">
          {incomeRanges.map((range) => (
            <button
              key={range.value}
              type="button"
              onClick={() => updateFinance({ income_range: range.value })}
              className={`p-2.5 rounded-lg border text-sm transition-colors ${
                finance.income_range === range.value
                  ? 'bg-[#C9A24A]/20 border-[#C9A24A] text-[#C9A24A]'
                  : 'bg-white/5 border-white/10 text-gray-300 hover:border-[#C9A24A]/50'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick toggles */}
      <div className="grid grid-cols-1 gap-2">
        <button type="button" onClick={() => toggleOption('has_contract')}
          className={`p-3 rounded-lg border flex items-center gap-3 transition-colors ${finance.has_contract ? 'bg-[#C9A24A]/20 border-[#C9A24A]' : 'bg-white/5 border-white/10 hover:border-[#C9A24A]/50'}`}>
          <FileText className={`w-4 h-4 ${finance.has_contract ? 'text-[#C9A24A]' : 'text-gray-400'}`} />
          <span className={`text-sm ${finance.has_contract ? 'text-[#C9A24A]' : 'text-gray-300'}`}>Tenho proposta/contrato de trabalho no destino</span>
        </button>
        <button type="button" onClick={() => toggleOption('has_health_insurance')}
          className={`p-3 rounded-lg border flex items-center gap-3 transition-colors ${finance.has_health_insurance ? 'bg-[#C9A24A]/20 border-[#C9A24A]' : 'bg-white/5 border-white/10 hover:border-[#C9A24A]/50'}`}>
          <Heart className={`w-4 h-4 ${finance.has_health_insurance ? 'text-[#C9A24A]' : 'text-gray-400'}`} />
          <span className={`text-sm ${finance.has_health_insurance ? 'text-[#C9A24A]' : 'text-gray-300'}`}>Tenho seguro de saude internacional</span>
        </button>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pt-2">
        <button type="button" data-testid="step2-back" onClick={prevStep} className="flex-1 btn-outline-gold">
          Voltar
        </button>
        <button type="button" data-testid="step2-next" onClick={nextStep} disabled={!isValid}
          className={`flex-1 btn-gold ${!isValid ? 'opacity-50 cursor-not-allowed' : ''}`}>
          Continuar
        </button>
      </div>
    </motion.div>
  );
};

export default Step2Perfil;
